/**
 * A single predefined sample of a method type, persisted under the
 * `defaultSamples` config key. The `name` is a language-keyed record (e.g.
 * `{ en: 'Sample A', es: 'Muestra A' }`) so readers can resolve the localized
 * label via `name[language]`.
 */
interface DefaultSample {
  name: Record<string, string>;
}

/**
 * Domain service that owns the shape of a method type's `config` blob. The
 * `MethodType` entity types `config` as `any`, so this service is the single
 * place that knows how to build and project its keys.
 */
export class MethodTypeConfigService {
  /**
   * Builds the `defaultSamples` config entries from the editor's per-sample
   * language records. Each input record becomes a `{ name: <record> }` entry,
   * copied so later mutations of the input cannot leak into the result.
   *
   * @param sampleNames - One localized record per predefined sample name
   * @returns The `defaultSamples` entries ready to persist on `config`
   */
  private static buildDefaultSamples(sampleNames: Record<string, string>[]): DefaultSample[] {
    return sampleNames.map((values) => ({ name: { ...values } }));
  }

  /**
   * Builds the full conventional `config` blob from the editor's per-sample
   * language records: `{ defaultSamples }` and nothing else. Shared by the
   * save command's default path and the editor's JSON-editor seed when the
   * user opens manual mode, so both produce a config `isValidConfig` accepts.
   *
   * @param sampleNames - One localized record per predefined sample name
   * @returns A conventional config owning only `defaultSamples`
   */
  public static buildConfig(sampleNames: Record<string, string>[]): Record<string, unknown> {
    return { defaultSamples: this.buildDefaultSamples(sampleNames) };
  }

  /**
   * Whether a config is "conventional" — faithfully editable by the predefined
   * sample-names chips UI rather than needing the raw JSON editor.
   *
   * `true` for an absent/empty config (`null`, `undefined`, or `{}` — a
   * pristine or legacy-empty method type) and for a config whose ONLY key is
   * `defaultSamples`, holding an array of `{ name }` entries where each `name`
   * is a record of localized strings. Anything else — foreign top-level keys
   * (e.g. `analysisLimit`), a non-array `defaultSamples`, or samples that are
   * not `{ name: localizedRecord }` — is "free" and returns `false`, which is
   * the editor's signal to open the JSON editor directly.
   *
   * @param config - The value to validate (typically a stored config blob)
   * @returns `true` when the chips editor can represent the config
   */
  public static isValidConfig(config: unknown): boolean {
    if (config === null || config === undefined) return true;
    if (!this.isPlainObject(config)) return false;

    const keys = Object.keys(config);
    if (keys.length === 0) return true;
    if (keys.length !== 1 || keys[0] !== 'defaultSamples') return false;

    const defaultSamples = config['defaultSamples'];
    if (!Array.isArray(defaultSamples)) return false;

    return defaultSamples.every((sample) => this.isValidSample(sample));
  }

  /**
   * Projects a config blob into the editor's localized sample-name records —
   * the inverse of {@link buildConfig}. Deliberately lenient where
   * `isValidConfig` is strict: legacy data stores bare-string samples,
   * plain-string names and null/empty language values, and this projection
   * normalizes all of them instead of rejecting the config. Samples that
   * yield no usable name are skipped; the returned records are fresh objects.
   *
   * @param config - A stored or drafted config blob (any shape tolerated)
   * @returns One localized record per recoverable predefined sample name
   */
  public static toSampleNames(config: unknown): Record<string, string>[] {
    const defaultSamples = (config as { defaultSamples?: unknown } | undefined | null)?.defaultSamples;
    if (!Array.isArray(defaultSamples)) return [];

    const sampleNames: Record<string, string>[] = [];
    for (const sample of defaultSamples) {
      const record = this.toSampleNameRecord(sample);
      if (record && Object.keys(record).length > 0) {
        sampleNames.push(record);
      }
    }
    return sampleNames;
  }

  private static toSampleNameRecord(sample: unknown): Record<string, string> | null {
    // Defensive: legacy data occasionally stores the bare string instead of { name }.
    if (typeof sample === 'string') return this.compactLanguageRecord({ en: sample });
    if (!sample || typeof sample !== 'object') return null;

    const name = (sample as { name?: unknown }).name;
    if (typeof name === 'string') return this.compactLanguageRecord({ en: name.trim() });
    if (name && typeof name === 'object') return this.compactLanguageRecord(name as Record<string, unknown>);
    return null;
  }

  /**
   * Shallow-copies a localized record keeping only non-empty string language
   * values (legacy fixtures store `pt: null`); always returns a fresh object
   * so callers can mutate the output without bleeding into the source.
   */
  private static compactLanguageRecord(values: Record<string, unknown>): Record<string, string> {
    const record: Record<string, string> = {};
    for (const [lang, value] of Object.entries(values)) {
      if (typeof value === 'string' && value.trim() !== '') {
        record[lang] = value;
      }
    }
    return record;
  }

  private static isValidSample(sample: unknown): boolean {
    if (!this.isPlainObject(sample)) return false;

    const keys = Object.keys(sample);
    if (keys.length !== 1 || keys[0] !== 'name') return false;

    return this.isLocalizedRecord(sample['name']);
  }

  private static isLocalizedRecord(name: unknown): boolean {
    return this.isPlainObject(name) && Object.values(name).every((value) => typeof value === 'string');
  }

  private static isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
